import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, RotateCcw, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HRReopenPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, expired, error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [requestingNewLink, setRequestingNewLink] = useState(false);

  useEffect(() => {
    processReopen();
  }, [token]);

  const processReopen = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hr-pipeline/reopen/${token}`);
      const data = await response.json();
      
      if (response.ok) {
        if (data.expired) {
          setResult(data);
          setStatus('expired');
        } else {
          setResult(data);
          setStatus('success');
        }
      } else {
        setError(data.detail || 'Invalid reopen link');
        setStatus('error');
      }
    } catch (err) {
      setError('Failed to process request');
      setStatus('error');
    }
  };

  const requestNewLink = async () => {
    setRequestingNewLink(true);
    try {
      // Extract share token from the review link path
      const reviewPath = result?.request_new_link || '';
      const shareToken = reviewPath.split('/').pop();
      
      if (!shareToken) {
        toast.error('Unable to request new link');
        return;
      }

      const response = await fetch(`${API_URL}/api/hr-pipeline/request-reopen-link/${shareToken}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('New reopen link sent to your email and WhatsApp!');
      } else {
        toast.error('Failed to send new link');
      }
    } catch (err) {
      toast.error('Failed to request new link');
    } finally {
      setRequestingNewLink(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">Processing your request...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RotateCcw className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Candidate Reopened</h1>
              <p className="text-slate-600 mb-6">
                {result?.message || 'The candidate has been reopened for review.'}
              </p>
              <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3 mb-6">
                Status: <strong className="text-amber-600">Pending Review</strong>
              </p>
              <Link to={result?.review_link || '/'}>
                <Button className="w-full">
                  Review Candidate Now
                </Button>
              </Link>
            </>
          )}
          
          {status === 'expired' && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Link Expired</h1>
              <p className="text-slate-600 mb-6">
                This reopen link has expired (valid for 24 hours).
              </p>
              <p className="text-sm text-slate-500 mb-6">
                You can request a new reopen link below. It will be sent to your email and WhatsApp.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  onClick={requestNewLink}
                  disabled={requestingNewLink}
                >
                  {requestingNewLink ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Request New Link
                </Button>
                <Link to={result?.request_new_link || '/'} className="block">
                  <Button variant="outline" className="w-full">
                    Go to Review Page
                  </Button>
                </Link>
              </div>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Link Invalid</h1>
              <p className="text-slate-600 mb-6">{error}</p>
              <p className="text-sm text-slate-500">
                This link may be invalid or the candidate may already have been reopened. Please contact the recruiter if you need assistance.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
